import { API_BASE_URL, CDN_URL } from './constants';

/**
 * Helper function to construct full image URL from relative path
 * @param {string} imageUrl - Relative image path (e.g., /uploads/products/filename.jpg)
 * @returns {string|null} - Full image URL or null if imageUrl is empty
 */
export const getImageUrl = (imageUrl) => {
  // Always log for debugging
  console.log('getImageUrl called with:', imageUrl);
  
  if (!imageUrl) {
    console.log('getImageUrl: imageUrl is null/empty');
    return null;
  }
  
  // Convert to string and trim
  const trimmedUrl = String(imageUrl).trim();
  
  // Fix malformed URLs that have localhost in front of https
  if (trimmedUrl.includes('localhost') && trimmedUrl.includes('https://')) {
    const fixedUrl = trimmedUrl.replace(/^https?:\/\/[^/]+\//, '').replace(/^\/+/, '');
    console.log('getImageUrl: Fixed malformed URL:', { original: trimmedUrl, fixed: fixedUrl });
    return fixedUrl;
  }
  
  // If it's already a full URL, return as is
  if (trimmedUrl.match(/^https?:\/\//i)) {
    console.log('getImageUrl: Already absolute URL:', trimmedUrl);
    return trimmedUrl;
  }
  
  // Use CDN URL if configured
  if (CDN_URL) {
    const cdnBase = CDN_URL.endsWith('/') ? CDN_URL.slice(0, -1) : CDN_URL;
    const normalizedPath = trimmedUrl.startsWith('/') ? trimmedUrl : `/${trimmedUrl}`;
    const fullUrl = `${cdnBase}${normalizedPath}`;
    console.log('getImageUrl: Using CDN URL:', fullUrl);
    return fullUrl;
  }
  
  // Get base URL without /api for static files
  // Images are served from /uploads route, not /api/uploads
  const apiBaseUrl = API_BASE_URL || process.env.REACT_APP_API_BASE_URL || 'https://api.essentick.com/api';
  console.log('getImageUrl: API_BASE_URL from constants:', API_BASE_URL);
  console.log('getImageUrl: apiBaseUrl:', apiBaseUrl);
  
  // Extract the base URL (protocol + host + port) without /api
  let baseUrl;
  if (apiBaseUrl.includes('/api')) {
    baseUrl = apiBaseUrl.replace('/api', '');
  } else {
    // If no /api in the URL, use it as is but ensure it doesn't end with /
    baseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
  }
  
  // Ensure baseUrl doesn't end with /
  if (baseUrl.endsWith('/')) {
    baseUrl = baseUrl.slice(0, -1);
  }
  
  // Ensure imageUrl starts with /
  const normalizedPath = trimmedUrl.startsWith('/') ? trimmedUrl : `/${trimmedUrl}`;
  
  // Construct full URL - MUST be absolute
  const fullUrl = `${baseUrl}${normalizedPath}`;
  
  console.log('getImageUrl: Constructed URL:', fullUrl);
  
  // Verify it's an absolute URL
  if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
    console.error('getImageUrl: Failed to construct absolute URL', {
      imageUrl: trimmedUrl,
      apiBaseUrl,
      baseUrl,
      normalizedPath,
      fullUrl
    });
    // Fallback: use default backend URL
    const fallbackUrl = `http://localhost:8000${normalizedPath}`;
    console.log('getImageUrl: Using fallback URL:', fallbackUrl);
    return fallbackUrl;
  }
  
  console.log('getImageUrl: Returning absolute URL:', fullUrl);
  return fullUrl;
};
